import {
  ApiACLRole,
  ApiClient,
  ApiProject,
  ApiUser,
  ApiAuthUser,
} from "@incendium/api";

// Type that represents a user with permissions (can be ApiUser or ApiAuthUser)
type UserWithPermissions = ApiUser | ApiAuthUser;

const isRole = (
  user: UserWithPermissions,
  role: ApiACLRole,
  clientId?: number,
  projectId?: number,
) =>
  (user.permissions || []).findIndex(
    (p) =>
      p.role === role &&
      (!clientId || p.clientId === clientId) &&
      (!projectId || p.projectId === projectId),
  ) >= 0;

export const hasPermission = (
  user: UserWithPermissions,
  role: ApiACLRole,
  selClient: ApiClient | null,
  selProject: ApiProject | null,
  write?: boolean,
) => {
  if (!user) {
    return false;
  }

  if (isRole(user, ApiACLRole.SUPER_ADMIN)) {
    return true;
  }

  switch (role) {
    case ApiACLRole.DOMAIN_ADMIN:
      return isRole(user, ApiACLRole.DOMAIN_ADMIN);
    case ApiACLRole.CLIENT_ADMIN:
      return (
        isRole(user, ApiACLRole.DOMAIN_ADMIN) ||
        isRole(user, ApiACLRole.CLIENT_ADMIN, selClient?.id)
      );
    case ApiACLRole.CLIENT_OBSERVER:
      return (
        isRole(user, ApiACLRole.DOMAIN_ADMIN) ||
        isRole(user, ApiACLRole.CLIENT_ADMIN, selClient?.id) ||
        isRole(user, ApiACLRole.CLIENT_OBSERVER, selClient?.id)
      );
    case ApiACLRole.PROJECT_ADMIN:
      return (
        isRole(user, ApiACLRole.DOMAIN_ADMIN) ||
        isRole(user, ApiACLRole.CLIENT_ADMIN, selClient?.id) ||
        (!write && isRole(user, ApiACLRole.CLIENT_OBSERVER, selClient?.id)) ||
        isRole(user, ApiACLRole.PROJECT_ADMIN, undefined, selProject?.id)
      );
    case ApiACLRole.PROJECT_OBSERVER:
      return (
        isRole(user, ApiACLRole.DOMAIN_ADMIN) ||
        isRole(user, ApiACLRole.CLIENT_ADMIN, selClient?.id) ||
        isRole(user, ApiACLRole.CLIENT_OBSERVER, selClient?.id) ||
        isRole(user, ApiACLRole.PROJECT_ADMIN, undefined, selProject?.id) ||
        isRole(user, ApiACLRole.PROJECT_OBSERVER, undefined, selProject?.id)
      );
    case ApiACLRole.ANALYTICS_ADMIN:
      return (
        isRole(user, ApiACLRole.DOMAIN_ADMIN) ||
        isRole(user, ApiACLRole.CLIENT_ADMIN, selClient?.id) ||
        (!write && isRole(user, ApiACLRole.CLIENT_OBSERVER, selClient?.id)) ||
        isRole(user, ApiACLRole.PROJECT_ADMIN, undefined, selProject?.id) ||
        (!write &&
          isRole(
            user,
            ApiACLRole.PROJECT_OBSERVER,
            undefined,
            selProject?.id,
          )) ||
        isRole(user, ApiACLRole.ANALYTICS_ADMIN, undefined, selProject?.id)
      );
    case ApiACLRole.ANALYTICS_OBSERVER:
      return (
        isRole(user, ApiACLRole.DOMAIN_ADMIN) ||
        isRole(user, ApiACLRole.CLIENT_ADMIN, selClient?.id) ||
        isRole(user, ApiACLRole.CLIENT_OBSERVER, selClient?.id) ||
        isRole(user, ApiACLRole.PROJECT_ADMIN, undefined, selProject?.id) ||
        isRole(user, ApiACLRole.PROJECT_OBSERVER, undefined, selProject?.id) ||
        isRole(user, ApiACLRole.ANALYTICS_ADMIN, undefined, selProject?.id) ||
        isRole(user, ApiACLRole.ANALYTICS_OBSERVER, undefined, selProject?.id)
      );

    default:
      return false;
  }
};

export function isTenantManagedUser(user?: ApiAuthUser | null): boolean {
  return Boolean((user?.tenant ?? "").trim());
}
